import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword } from '../services/cognitoService';
import { 
  Mail, ArrowLeft, CheckCircle, AlertCircle, Lock,
  Atom, Beaker, BookOpen, GraduationCap, 
  Microscope, Pencil, Ruler, Lightbulb, 
  Globe, Settings, Brain, Calculator,
  Dna, Orbit, Sigma, Box, Binary,
  Layers, Variable, Pi, CircleDashed, Cpu,
  Search, Star, Trophy, FlaskConical, Thermometer,
  Database, Code, Terminal, Infinity, Zap,
  Puzzle, MousePointer2, ClipboardList
} from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Stable random icons for the background
  const backgroundIcons = React.useMemo(() => {
    const iconTypes = [
      Atom, Beaker, BookOpen, GraduationCap, Microscope, Pencil, Ruler, Lightbulb, 
      Globe, Settings, Brain, Calculator, Dna, Orbit, Sigma, Box, Binary, 
      Layers, Variable, Pi, CircleDashed, Cpu, Search, Star, Trophy, 
      FlaskConical, Thermometer, Database, Code, Terminal, Infinity, Zap,
      Puzzle, MousePointer2, ClipboardList
    ];
    
    const rows = 16;
    const cols = 16;
    const items = [];
    
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const index = r * cols + c;
        items.push({
          Icon: iconTypes[index % iconTypes.length],
          top: `${(r / rows) * 100 + (Math.random() * 4 - 2)}%`,
          left: `${(c / cols) * 100 + (Math.random() * 4 - 2)}%`,
          rotate: `${(Math.random() - 0.5) * 40}deg`,
          scale: 0.8 + Math.random() * 0.5,
          opacity: 0.1 + Math.random() * 0.15
        });
      }
    }
    return items;
  }, []);

  // Password strength checker
  const getPasswordStrength = (pwd) => {
    const checks = {
      length: pwd.length >= 8,
      lowercase: /[a-z]/.test(pwd),
      uppercase: /[A-Z]/.test(pwd),
      number: /\d/.test(pwd),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)
    };
    
    const passedChecks = Object.values(checks).filter(Boolean).length;
    
    let strength = 'weak';
    if (passedChecks === 5) strength = 'strong';
    else if (passedChecks >= 3) strength = 'medium';
    
    return { strength, passedChecks, totalChecks: 5, checks };
  };

  const passwordStrength = getPasswordStrength(newPassword);
  
  const strengthColors = {
    weak: 'bg-red-500',
    medium: 'bg-yellow-500',
    strong: 'bg-green-500'
  };
  
  const strengthLabels = {
    weak: 'Weak',
    medium: 'Medium',
    strong: 'Strong'
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      await forgotPassword(email);
      setCodeSent(true);
      setMessage('Reset code sent! Check your email.');
    } catch (err) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (passwordStrength.strength !== 'strong') {
      setError('Password must include lowercase, uppercase, number, special character, and be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      const { confirmForgotPassword } = await import('../services/cognitoService');
      await confirmForgotPassword(email, confirmationCode, newPassword);
      setMessage('Password reset successful! Redirecting to sign in...');
      setTimeout(() => {
        window.location.href = '/signin';
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0d47a1] via-[#1565c0] to-[#0d47a1] relative overflow-hidden py-12 px-4 sm:px-6 lg:px-8">
      {/* Randomized Educational Icon Pattern Background */}
      <div className="absolute inset-0 pointer-events-none select-none">
        {backgroundIcons.map((item, i) => (
          <div 
            key={i} 
            className={`absolute pointer-events-auto cursor-default group 
              ${i % 3 === 0 ? 'hidden sm:block' : ''} 
              ${i % 5 === 0 ? 'hidden md:block' : ''} 
              ${i % 7 === 0 ? 'hidden lg:block' : ''}`}
            style={{ 
              top: item.top,
              left: item.left,
              transform: `rotate(${item.rotate}) scale(${item.scale})`,
              opacity: item.opacity,
              zIndex: 0
            }}
          >
            <div className="transition-all duration-500 ease-out group-hover:scale-[1.5] group-hover:rotate-[15deg] group-hover:opacity-100 text-white">
              <item.Icon className="w-5 h-5 sm:w-10 sm:h-10 md:w-12 md:h-12 drop-shadow-[0_0_8px_rgba(255,255,255,0.2)] group-hover:drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]" strokeWidth={1.5} />
            </div>
          </div>
        ))}
      </div>

      {/* Radiant Glows */}
      <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-blue-400/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-indigo-400/20 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-md w-full space-y-8 relative z-10">
        <div className="text-center">
          <div className="flex justify-center items-center space-x-3 mb-8">
            <img 
              src="/logo/nep-logo.png" 
              alt="NEP" 
              className="h-16 w-auto drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]"
            />
            <div className="flex flex-col text-left border-l-2 border-white/20 pl-4">
              <span className="text-4xl font-bold text-white tracking-tight">NEP</span>
              <span className="text-xs font-semibold text-blue-200 tracking-wider">NextGen Exam Platform</span>
            </div>
          </div>
        </div>

        {/* Glass Card */}
        <div className="bg-white/[0.08] backdrop-blur-3xl border border-white/20 rounded-3xl p-10 shadow-[0_20px_50px_rgba(0,0,0,0.3)]">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white">Reset Password</h2>
            <p className="text-blue-100 text-sm mt-2 font-medium">
              {!codeSent ? "Enter your email to receive a reset code" : "Enter the code and your new password"}
            </p>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center space-x-2 mb-6">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <span className="text-sm text-red-200">{error}</span>
            </div>
          )}

          {message && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 flex items-center space-x-2 mb-6">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <span className="text-sm text-green-200">{message}</span>
            </div>
          )}

          {!codeSent ? (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1.5 ml-1">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-blue-400/70" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/60 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                    placeholder="Email ID"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 px-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl font-semibold shadow-lg shadow-blue-900/20 transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
                    <span>Sending code...</span>
                  </>
                ) : (
                  <span>Send Reset Code</span>
                )}
              </button>

              <div className="text-center pt-2">
                <Link to="/signin" className="inline-flex items-center text-sm font-medium text-white/60 hover:text-white transition-colors">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Sign In
                </Link>
              </div>
            </form>
          ) : (
            <form className="space-y-5" onSubmit={handleResetPassword}>
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-300 mb-1.5 ml-1">
                  Verification Code
                </label>
                <input
                  id="code"
                  name="code"
                  type="text"
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/60 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                  placeholder="Enter code"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-1.5 ml-1">
                  New Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-blue-400/70" />
                  </div>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    required
                    className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/60 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
                {/* Password Strength Meter */}
                {newPassword && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-300 ${strengthColors[passwordStrength.strength]}`}
                          style={{ width: `${(passwordStrength.passedChecks / passwordStrength.totalChecks) * 100}%` }}
                        />
                      </div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${
                        passwordStrength.strength === 'weak' ? 'text-red-400' :
                        passwordStrength.strength === 'medium' ? 'text-yellow-400' :
                        'text-green-400'
                      }`}>
                        {strengthLabels[passwordStrength.strength]}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-1.5 ml-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-blue-400/70" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/60 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 px-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl font-semibold shadow-lg shadow-blue-900/20 transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
                    <span>Resetting...</span>
                  </>
                ) : (
                  <span>Reset Password</span>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
